import { FC, ChangeEvent, useState, useEffect } from 'react'
import Avatar from 'react-avatar'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import 'react-datepicker/dist/react-datepicker.css'
import './style.scss'
import CONFIG from '../../../config'
import { IForecast, IRegion, ICommune, ICity, FormResponseStatus, INationality } from '../../models'
import { utils } from '../../utils'
import { Text, Input, Textarea, Button, FormLabelError, Select, DatePicker, Icon, FormMessageError, FormMessageSuccess } from '../UI'
import { Modal, useModal } from '../../components/UI/Modal'
import { useCities, useCommunes, useRegions, useForecasts, useUserGenders, useUserNationalities } from '../../hooks'
import { userService } from '../../services'
import { authActions } from '../../store/actions'

type ProfileFormProps = {
  className?: string,
  user: any,
  userAvatar?: string,
  onChange?(): void
}
export interface IProfileForm {
  file?: any,
  firstName: string,
  lastName: string,
  secondName: string,
  dateBirth: any,
  nationalityId: string,
  forecastId: string,
  genderId: string,
  email: string,
  regionId: string,
  cityId: string,
  communeId: string,
  address: string,
  phone: string
}

const FormLabelView:FC<{ className?: string, disabled?: boolean }> = ({ children, className, disabled = false }) => <Text component="label" color="primary" className={className} style={{ opacity: (disabled ? '.4' : '') }}>{children}</Text>

const TextDisabled:FC = ({ children }) => <Text size="14" className="px-10" style={{ opacity: '.4' }}>{children}</Text>

const ProfileForm:FC<ProfileFormProps> = ({ className = '', user, userAvatar, onChange }) => {
  const defaultValues: IProfileForm = {
    file: '',
    firstName: (user ? user.first_name : ''),
    lastName: (user ? user.last_name : ''),
    secondName: (user ? user.second_name : ''),
    dateBirth: (user ? new Date(`${utils.getDateFormat(user.date_birth)}`) : ''),
    nationalityId: (user ? user.nationality_id : ''),
    forecastId: (user ? user.forecast_id : ''),
    genderId: (user ? String(user.gender_id) : ''),
    email: (user ? user.email : ''),
    regionId: (user ? user.region?.region_id : ''),
    cityId: (user ? user.city?.city_id : ''),
    communeId: (user ? user.commune?.commune_id : ''),
    address: (user ? user.address : ''),
    phone: (user ? user.phone : '')
  }
  const { register, handleSubmit, formState: { errors }, getValues, setValue, setFocus, clearErrors, control, setError } = useForm<IProfileForm>({ defaultValues })
  const dispatch = useDispatch()
  const modal = useModal(false)
  const [respStatus, setRespStatus] = useState<FormResponseStatus>('')
  const [submited, setSubmited] = useState<boolean>(false)
  const [avatarImg, setAvatarImg] = useState<string>('')

  const nationalities = useUserNationalities()
  const regions = useRegions()
  const cities = useCities()
  const communes = useCommunes()
  const forecasts = useForecasts()
  const genders = useUserGenders()

  useEffect(() => {
    cities.getCities(Number(defaultValues.regionId))
    communes.getCommunes(Number(defaultValues.cityId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setForecastId()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecasts])

  const setForecastId = () => {
    setValue('forecastId', defaultValues.forecastId)
  }

  const postForm = async (formPro: FormData) => {
    const resp: any = await userService.updateUserCurrent(formPro)

    if(resp && resp.status === 'Ok') {
      const { patient } = resp.data

      setRespStatus('SUCCESS')
      if(patient) {
        dispatch(authActions.updateUserCurrentStorage(patient))
        if(onChange) onChange()
      }
    } else {
      setRespStatus('ERROR')
    }
    modal.open()
    setSubmited(false)
  }

  const onFileChange = (event: any) => {
    const { target: { name, files } } = event

    if(files && files[0]) {
      const fieldName = name
      const fileType = files[0].type
      const fileSize = files[0].size

      if(fileType && (fileType.indexOf('png') === -1 && fileType.indexOf('jpeg') === -1)) {
        setError(fieldName, {
          type: 'format',
          message: 'Selecciona formato png ó jpg'
        })
        return ;
      } else if(fileSize && (fileSize / 1024 > 500)) {
        setError(fieldName, {
          type: 'size',
          message: 'Selecciona tamaño máximo 500KB'
        })
        return ;
      }

      if(name === 'file') {
        const image = URL.createObjectURL(files[0])
        setAvatarImg(image)
      } else {
        setValue(fieldName, files)
      }
      clearErrors(fieldName)
    }
  }

  const onSecondNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event
    const fieldName = 'secondName'

    if(value) clearErrors('lastName')

    setValue(fieldName, value)
    setTimeout(() => setFocus(fieldName), 0)
  }

  const onRegionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { target: { value } } = event
    cities.getCities(Number(value))
    communes.setEmpty()
    setValue('cityId', '')
    setValue('communeId', '')
    if(value) clearErrors('regionId')
  }

  const onCityChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { target: { value } } = event
    communes.getCommunes(Number(value))
    setValue('communeId', '')
    if(value) clearErrors('cityId')
  }

  const onSubmit = async (fData: IProfileForm) => {
    const {
      file,
      firstName,
      lastName,
      secondName,
      dateBirth,
      nationalityId,
      forecastId,
      genderId,
      email,
      regionId,
      cityId,
      communeId,
      address,
      phone
    } = fData
    const formPro:FormData = new FormData()

    formPro.append('file', (file ? file[0] : ''))
    formPro.append('first_name', firstName)
    formPro.append('last_name', lastName)
    formPro.append('second_name', secondName)
    formPro.append('date_birth', `${utils.getDateFormat(dateBirth, 'YYYY-MM-DD')}`)
    formPro.append('nationality_id', nationalityId)
    formPro.append('forecast_id', forecastId)
    formPro.append('gender_id', genderId)
    formPro.append('email', email)
    formPro.append('region_id', regionId)
    formPro.append('city_id', cityId)
    formPro.append('commune_id', communeId)
    formPro.append('address', address)
    formPro.append('phone', phone)

    setSubmited(true)
    postForm(formPro)
  }

  return (
    <>
      <div className={className}>
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-12 lg:col-6">
              <div>
                <div className="register-form-avatar">
                  <Avatar
                    src={avatarImg ? avatarImg : userAvatar}
                    className="profile-avatar"
                  />
                  <div className="register-form-avatar_icon">
                    <Icon name="photo" />
                    <input
                      {...register('file')}
                      type="file"
                      id="file"
                      name="file"
                      accept="image/x-png,image/jpeg"
                      onChange={onFileChange}
                    />
                  </div>
                </div>
                <Text component="label" size="12" className="mt-5">Imagen con formato .jpg, .png y máximo 500KB</Text>
                {errors.file && <FormLabelError>{errors.file?.message}</FormLabelError>}
              </div>

              <div className="row mt-30">
                <div className="col-12 lg:col-5 lg:pt-5 lg:pr-0">
                  <FormLabelView>Nombres</FormLabelView>
                </div>
                <div className="col-12 lg:col-7 mt-5 lg:mt-0">
                  <Input
                    {...register('firstName', { required: 'Digita Nombre' })}
                    id="firstName"
                    name="firstName"
                    placeholder="Nombre"
                    tabIndex={1}
                    fullWidth
                    error={!!errors.firstName}
                  />
                  {errors.firstName && <FormLabelError>{errors.firstName?.message}</FormLabelError>}
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-5 lg:pt-5 lg:pr-0">
                  <FormLabelView>Primer apellido</FormLabelView>
                </div>
                <div className="col-12 lg:col-7 mt-5 lg:mt-0">
                  <Input
                    {...register('lastName', {
                      validate: {
                        required: (value) => {
                          if (!value && !getValues('secondName')) return 'Digita Primer apellido'
                          return true
                        }
                      }
                    })}
                    id="lastName"
                    name="lastName"
                    placeholder="Apellido"
                    tabIndex={2}
                    fullWidth
                    error={!!errors.lastName}
                  />
                  {errors.lastName && <FormLabelError>{errors.lastName?.message}</FormLabelError>}
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-5 lg:pt-5 lg:pr-0">
                  <FormLabelView>Segundo apellido</FormLabelView>
                </div>
                <div className="col-12 lg:col-7 mt-5 lg:mt-0">
                  <Input
                    {...register('secondName')}
                    id="secondName"
                    name="secondName"
                    placeholder="Apellido"
                    tabIndex={3}
                    fullWidth
                    onChange={onSecondNameChange}
                  />
                </div>
              </div>

              <div className="row mt-25" style={{ minHeight: 30 }}>
                <div className="col-12 lg:col-5 lg:pt-5 lg:pr-0">
                  <FormLabelView disabled>Tipo de documento</FormLabelView>
                </div>
                <div className="col-12 lg:col-7 mt-5 lg:mt-0 pt-5">
                  <TextDisabled>{user.documentType?.name}</TextDisabled>
                </div>
              </div>

              <div className="row mt-25" style={{ minHeight: 30 }}>
                <div className="col-12 lg:col-5 lg:pt-5 lg:pr-0">
                  <FormLabelView disabled>Número de documento</FormLabelView>
                </div>
                <div className="col-12 lg:col-7 mt-5 lg:mt-0 pt-5">
                  <TextDisabled>{user.document_number}</TextDisabled>
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-5 lg:pt-5 lg:pr-0">
                  <FormLabelView>Fecha de nacimiento</FormLabelView>
                </div>
                <div className="col-12 lg:col-7 mt-5 lg:mt-0">
                  <Controller
                    control={control}
                    name='dateBirth'
                    render={({ field }) => (
                      <DatePicker
                        {...register('dateBirth', { required: 'Selecciona Fecha de nacimiento' })}
                        selected={field.value}
                        placeholderText="DD/MM/YYYY"
                        tabIndex={4}
                        openToDate={new Date('2000/01/01')}
                        showYearDropdown
                        showMonthDropdown
                        fullWidth
                        error={!!errors.dateBirth}
                        onChange={(date) => field.onChange(date)}
                      />
                    )}
                  />
                  {errors.dateBirth && <FormLabelError>Selecciona Fecha de nacimiento</FormLabelError>}
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-5 lg:pt-5 lg:pr-0">
                  <FormLabelView>Previsión</FormLabelView>
                </div>
                <div className="col-12 lg:col-7 mt-5 lg:mt-0">
                  {forecasts.data ?
                      <Select
                        {...register('forecastId', { required: 'Selecciona Previsión' })}
                        id="forecastId"
                        name="forecastId"
                        defaultValue=""
                        tabIndex={5}
                        fullWidth
                        required
                        error={!!errors.forecastId}
                      >
                        <option value="" disabled>Selecciona</option>
                        {forecasts.data.map((it: IForecast) => <option key={it.id} value={it.id} hidden={it.status == 0 ? true : false}>{it.nombre}</option>)}
                      </Select>
                    :
                      <Select
                        id="forecastIdTmp"
                        name="forecastIdTmp"
                        tabIndex={5}
                        fullWidth
                        required
                        loading={true}
                        error={!!errors.forecastId}
                      >
                        <option value="" disabled>Selecciona</option>
                      </Select>
                  }
                  {errors.forecastId && <FormLabelError>{errors.forecastId?.message}</FormLabelError>}
                </div>
              </div>
            </div>

            <div className="col-12 lg:col-6 lg:pl-40 mt-25 lg:mt-0">
              <div className="row">
                <div className="col-12 lg:col-3 lg:pt-5">
                  <FormLabelView>E-mail</FormLabelView>
                </div>
                <div className="col-12 lg:col-9 mt-5 lg:mt-0">
                  <Input
                    {...register('email', { required: 'Digita E-mail', pattern: { value: CONFIG.FORM.PATTERN.EMAIL, message: 'Digita E-mail válido' } })}
                    id="email"
                    name="email"
                    placeholder="E-mail"
                    tabIndex={6}
                    fullWidth
                    error={!!errors.email}
                  />
                  {errors.email && <FormLabelError>{errors.email?.message}</FormLabelError>}
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-3 lg:pt-5">
                  <FormLabelView>Teléfono</FormLabelView>
                </div>
                <div className="col-12 lg:col-9 mt-5 lg:mt-0">
                  <Input
                    {...register('phone', { required: 'Digita Teléfono', pattern: { value: CONFIG.FORM.PATTERN.PHONE.EXP, message: 'Digita Teléfono válido' }})}
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="9XXXXXXXX"
                    maxLength={CONFIG.FORM.PATTERN.PHONE.MAXLENGHT}
                    tabIndex={7}
                    fullWidth
                    error={!!errors.phone}
                  />
                  {errors.phone && <FormLabelError>{errors.phone?.message}</FormLabelError>}
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-3 lg:pt-5">
                  <FormLabelView>Dirección</FormLabelView>
                </div>
                <div className="col-12 lg:col-9 mt-5 lg:mt-0">
                  <Textarea
                    {...register('address', { required: 'Digita Dirección' })}
                    id="address"
                    name="address"
                    tabIndex={8}
                    defaultValue={user.address}
                    fullWidth
                    error={!!errors.address}
                    style={{ minHeight: '50px' }}
                  />
                  {errors.address && <FormLabelError>{errors.address?.message}</FormLabelError>}
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-3 lg:pt-5">
                  <FormLabelView>Nacionalidad</FormLabelView>
                </div>
                <div className="col-12 lg:col-9 mt-5 lg:mt-0 fz-0">
                  {nationalities.data ?
                      <Select
                        {...register('nationalityId', { required: 'Selecciona Nacionalidad' })}
                        id="nationalityId"
                        name="nationalityId"
                        tabIndex={9}
                        fullWidth
                        required
                        error={!!errors.nationalityId}
                      >
                        <option value="" disabled>Selecciona</option>
                        {nationalities.data.map((it: INationality) => <option key={it.id} value={it.id}>{it.name}</option>)}
                      </Select>
                    :
                      <Select
                        id="nationalityIdTmp"
                        name="nationalityIdTmp"
                        tabIndex={9}
                        fullWidth
                        required
                        loading={true}
                        error={!!errors.nationalityId}
                      >
                        <option value="" disabled>Selecciona</option>
                      </Select>
                  }
                  {errors.nationalityId && <FormLabelError>{errors.nationalityId?.message}</FormLabelError>}
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-3 lg:pt-5">
                  <FormLabelView>Región</FormLabelView>
                </div>
                <div className="col-12 lg:col-9 mt-5 lg:mt-0 fz-0">
                  {regions.data && regions.data.length ?
                      <Select
                        {...register('regionId', { required: 'Selecciona Región' })}
                        id="regionId"
                        name="regionId"
                        tabIndex={10}
                        fullWidth
                        required
                        error={!!errors.regionId}
                        onChange={onRegionChange}
                      >
                        <option value="" disabled>Selecciona</option>
                        {regions.data.map((it: IRegion) => <option key={it.id} value={it.id}>{it.name}</option>)}
                      </Select>
                    :
                      <Select
                        id="regionIdTmp"
                        name="regionIdTmp"
                        tabIndex={10}
                        fullWidth
                        required
                        loading={true}
                        error={!!errors.regionId}
                      >
                        <option value="" disabled>Selecciona</option>
                      </Select>
                  }
                  {errors.regionId && <FormLabelError>{errors.regionId?.message}</FormLabelError>}
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-3 lg:pt-5">
                  <FormLabelView>Provincia</FormLabelView>
                </div>
                <div className="col-12 lg:col-9 mt-5 lg:mt-0 fz-0">
                  {cities.data && cities.data.length ?
                      <Select
                        {...register('cityId', { required: 'Selecciona Provincia'})}
                        id="cityId"
                        name="cityId"
                        defaultValue={defaultValues.cityId}
                        tabIndex={11}
                        fullWidth
                        required
                        error={!!errors.cityId}
                        onChange={onCityChange}
                      >
                        <option value="">Selecciona</option>
                        {cities.data.map((it: ICity) => <option key={it.id} value={it.id}>{it.name}</option>)}
                      </Select>
                    :
                      <Select
                        id="cityIdTmp"
                        name="cityIdTmp"
                        defaultValue=""
                        tabIndex={11}
                        fullWidth
                        required
                        error={!!errors.cityId}
                        disabled={true}
                        loading={cities.data === null}
                      >
                        <option value="">Selecciona</option>
                      </Select>
                  }
                  {errors.cityId && <FormLabelError>{errors.cityId?.message}</FormLabelError>}
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-3 lg:pt-5">
                  <FormLabelView>Comuna</FormLabelView>
                </div>
                <div className="col-12 lg:col-9 mt-5 lg:mt-0 fz-0">
                  {communes.data && communes.data.length ?
                      <Select
                        {...register('communeId', { required: 'Selecciona Comuna' })}
                        id="communeId"
                        name="communeId"
                        defaultValue={defaultValues.communeId}
                        tabIndex={12}
                        fullWidth
                        required
                        error={!!errors.communeId}
                      >
                        <option value="">Selecciona</option>
                        {communes.data.map((it: ICommune) => <option key={it.id} value={it.id}>{it.name}</option>)}
                      </Select>
                    :
                      <Select
                        id="communeIdTmp"
                        name="communeIdTmp"
                        defaultValue=""
                        tabIndex={12}
                        fullWidth
                        required
                        error={!!errors.communeId}
                        disabled={true}
                        loading={communes.data === null}
                      >
                        <option value="">Selecciona</option>
                      </Select>
                  }
                  {errors.communeId && <FormLabelError>{errors.communeId?.message}</FormLabelError>}
                </div>
              </div>

              <div className="row mt-25">
                <div className="col-12 lg:col-3 lg:pt-5">
                  <FormLabelView>Género</FormLabelView>
                </div>
                <div className="col-12 lg:col-9 mt-5 lg:mt-0 fz-0">
                  {genders.data ?
                      <Select
                        {...register('genderId', { required: 'Selecciona Género' })}
                        id="genderId"
                        name="genderId"
                        tabIndex={13}
                        fullWidth
                        required
                        error={!!errors.genderId}
                      >
                        <option value="" disabled>Selecciona</option>
                        {genders.data.map((it: ICommune) => <option key={it.id} value={it.id}>{it.name}</option>)}
                      </Select>
                    :
                      <Select
                        id="genderIdTmp"
                        name="genderIdTmp"
                        tabIndex={13}
                        fullWidth
                        required
                        loading={true}
                        error={!!errors.genderId}
                      >
                        <option value="" disabled>Selecciona</option>
                      </Select>
                  }
                  {errors.genderId && <FormLabelError>{errors.genderId?.message}</FormLabelError>}
                </div>
              </div>
            </div>
          </div>

          <div className="text-right mt-40">
            <Button type="submit" disabled={submited} className="w-full lg:w-auto">GUARDAR CAMBIOS</Button>
          </div>

          <div className="mt-30 lg:mt-20">
            <Text size="12" color="gray-400">* Si deseas cambiar el tipo de documento dirígete a servicio al cliente.</Text>
          </div>
        </form>
      </div>

      <Modal show={modal.show} maxWidth="345px" onClose={() => modal.close()}>
        {respStatus === 'SUCCESS' &&
          <FormMessageSuccess title="¡Los cambios fueron guardados con éxito!" className="pt-80 pb-100 px-15" />
        }
        {respStatus === 'ERROR' &&
          <FormMessageError
            message="No ha sido posible actulizar los datos"
            className="pt-80 pb-70 px-15"
            onClick={() => modal.close()}
          />
        }
      </Modal>
    </>
  )
}

export { ProfileForm }